import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import Select from "@/components/ui/Select";
import CollapsibleTagList from "@/components/shared/CollapsibleTagList";
import { cn } from "@/lib/utils";
import { flattenErrors } from "@/utils/formErrorUtils";

/**
 * RHFSelect — the app's `Select`, wired to react-hook-form.
 *
 * This used to carry its own copy of the dropdown: trigger, portal, placement
 * maths, search box, outside-click and scroll handlers, ~180 lines that were
 * `components/ui/Select.jsx` with different class names. The two had already
 * drifted, and every fix had to be made twice — keyboard navigation was the
 * one that made it obvious, because the first pass added it to one copy and
 * left Country and Currency on this screen still dead to the arrow keys.
 *
 * All that is left here is what react-hook-form actually needs: the label and
 * its required marker, the `Controller` binding, and the field error. The
 * `className` restores the taller form-field trigger — a form row is not the
 * same height as a toolbar control, and that difference was the only real
 * reason the two components were ever separate.
 *
 * @param {string} name - Field name in the form.
 * @param {string} label - Label above the control. Still drives the
 *   accessible name (`aria-label`) even when `hideLabel` suppresses it visually.
 * @param {boolean} [hideLabel] - Skips rendering the visible `<label>` — for a
 *   caller that draws its own label elsewhere (e.g. beside a row-level action)
 *   and would otherwise show it twice.
 * @param {React.ReactNode} [labelAddon] - Drawn on the label's line, after it —
 *   e.g. an `InfoTip` holding the field's notes. Sits beside the `<label>`
 *   rather than inside it, because a button inside a label is interactive
 *   content the label would also activate.
 * @param {Array<{label: string, value: any}>} options - What to choose from.
 * @param {string} [placeholder='Select'] - Shown until a value is picked.
 * @param {boolean} [required] - Draws the asterisk.
 * @param {boolean} [disabled] - The field does not apply. Goes to react-hook-form's
 *   `Controller`, and RHF's contract for that is "**omit this value from the
 *   submission**" — a disabled field submits `undefined`, not its value.
 * @param {boolean} [readOnly] - The value applies and MUST submit, but this user
 *   may not change it. Greys the control exactly like `disabled` and never
 *   reaches `Controller`, so the value survives the submit.
 *
 *   The two are easy to confuse and one of them eats data. Reach for `readOnly`
 *   whenever the field is showing an answer the form still has to send — a value
 *   the job fixed, a field only an admin may edit. `disabled` is for a field that
 *   genuinely has no answer to give, and for the moment a submit is in flight
 *   (harmless there: `handleSubmit` has already read the values).
 * @param {string} [fallbackLabel] - Shown when the field holds a value that is not in `options`.
 * @param {boolean} [creatable] - With `onCreate`, a typed term matching no option offers `Add "<term>"`.
 * @param {(term: string) => void} [onCreate] - Called with the trimmed term when the add row is committed.
 * @param {boolean} [multiple] - The field holds an array; see `Select`'s `multiple`.
 * @param {boolean} [chips] - With `multiple`, shows each pick as a removable
 *   badge above the trigger, and the trigger keeps its placeholder.
 * @param {(value: any) => void} [onValueChange] - Called after the field is written.
 * @param {any} [value] - Overrides the displayed value (defaults to the RHF field's own value).
 *   For a control whose visible selection isn't itself a single form field — e.g. one sentinel
 *   value stands in for a combination of two other fields — pass the derived value here.
 * @param {(value: any) => void} [onChange] - Overrides how a selection is written (defaults to
 *   the RHF field's own onChange). Pairs with `value` above; the caller is then responsible for
 *   writing whatever fields the selection actually maps to (typically via `setValue`).
 */
const RHFSelect = ({
  name,
  label,
  hideLabel = false,
  labelAddon,
  options = [],
  className,
  placeholder = "Select",
  required = false,
  disabled = false,
  readOnly = false,
  searchable = false,
  searchPlaceholder = "Search...",
  fallbackLabel,
  creatable = false,
  onCreate,
  multiple = false,
  chips = false,
  onValueChange,
  value: valueOverride,
  onChange: onChangeOverride,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const flattened = flattenErrors(errors);

  return (
    <div>
      {label && !hideLabel && (
        <div className="flex items-center gap-1 mb-1.5">
          <label className="block text-sm text-text-secondary" htmlFor={name}>
            {label}
            {required && <span className="text-red-500 font-bold text-[16px] ml-1">*</span>}
          </label>
          {labelAddon}
        </div>
      )}

      <Controller
        name={name}
        control={control}
        // `readOnly` deliberately does NOT go here — see the prop docs above.
        disabled={disabled}
        render={({ field }) => (
          <>
            {chips && multiple && (
              // The picks as removable badges, above the trigger, which then
              // keeps its placeholder rather than repeating them.
              <div className="mb-2">
                <CollapsibleTagList
                  values={(field.value ?? []).map((v) => options.find((o) => o.value === v)?.label ?? String(v))}
                  onRemove={disabled || readOnly ? undefined : (i) => field.onChange((field.value ?? []).filter((_, j) => j !== i))}
                />
              </div>
            )}
            <Select
              id={name}
              // The visible `<label htmlFor>` above loses to `Select`'s own
              // `aria-label` fallback (`ariaLabel || placeholder`) either way —
              // `aria-label` outranks native label association in the
              // accessible-name computation — so the field's real label has to
              // be handed down explicitly, or every RHFSelect reads by its
              // placeholder instead of its label.
              aria-label={label}
              value={valueOverride !== undefined ? valueOverride : field.value}
              onChange={(value) => {
                if (onChangeOverride) {
                  onChangeOverride(value);
                } else {
                  field.onChange(value);
                }
                onValueChange?.(value);
              }}
              onBlur={field.onBlur}
              options={options}
              placeholder={placeholder}
              disabled={disabled || readOnly}
              searchable={searchable}
              searchPlaceholder={searchPlaceholder}
              fallbackLabel={fallbackLabel}
              creatable={creatable}
              onCreate={onCreate}
              multiple={multiple}
              hasError={!!flattened[name]}
              // The form-field look, restored on top of the toolbar-sized
              // trigger. `h-auto` is load-bearing: it drops the button's own
              // `h-8` so the padding decides the height, which is what lines
              // this row up with the `RHFInput` beside it.
              className={cn("h-auto px-4 py-2.5 font-normal bg-background-form/40", className)}
              triggerLabel={chips && multiple ? placeholder : undefined}
            />
          </>
        )}
      />

      {flattened[name] && <p className="text-red-400 text-xs mt-1">{flattened[name]?.message}</p>}
    </div>
  );
};

export default RHFSelect;
